import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class ChkButtonPaymentsPlugin implements Plugin {
  private readonly buttons: NodeListOf<HTMLButtonElement>;

  constructor(private readonly element: HTMLElement) {
    this.buttons = element.querySelectorAll(
      '[data-plugin="chk-button-payments"]',
    );
  }

  init(): void {
    if (this.buttons.length === 0) return;

    this.buttons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleButtonClick(button);
      });
    });

    this.syncPanelsVisibility();
  }

  private handleButtonClick(button: HTMLButtonElement): void {
    const currentState = button.getAttribute("data-state") || "default";

    if (currentState === "disabled") {
      return;
    }

    if (currentState === "selected") {
      this.deselect(button);
      this.hidePanelFor(button);
    } else {
      this.deselectAll(button);
      this.select(button);
      this.showPanelFor(button);
    }
  }

  private deselectAll(except?: HTMLButtonElement): void {
    this.buttons.forEach((btn) => {
      if (btn !== except && btn.getAttribute("data-state") === "selected") {
        this.deselect(btn);
        this.hidePanelFor(btn);
      }
    });
  }

  private select(button: HTMLButtonElement): void {
    button.setAttribute("data-state", "selected");
    button.setAttribute("aria-pressed", "true");
    button.classList.add("chk-button-payments--selected");
    this.updateIcon(button, true);

    button.dispatchEvent(
      new CustomEvent("chkButtonPayments:selected", {
        detail: { id: button.id },
        bubbles: true,
      }),
    );
  }

  private deselect(button: HTMLButtonElement): void {
    button.setAttribute("data-state", "default");
    button.setAttribute("aria-pressed", "false");
    button.classList.remove("chk-button-payments--selected");
    this.updateIcon(button, false);

    button.dispatchEvent(
      new CustomEvent("chkButtonPayments:deselected", {
        detail: { id: button.id },
        bubbles: true,
      }),
    );
  }

  private showPanelFor(button: HTMLButtonElement): void {
    const targetId = button.getAttribute("data-target");
    if (!targetId) return;

    const panel = document.getElementById(targetId);
    if (panel) {
      panel.hidden = false;
    }
  }

  private hidePanelFor(button: HTMLButtonElement): void {
    const targetId = button.getAttribute("data-target");
    if (!targetId) return;

    const panel = document.getElementById(targetId);
    if (panel) {
      panel.hidden = true;
    }
  }

  private syncPanelsVisibility(): void {
    this.buttons.forEach((button) => {
      const targetId = button.getAttribute("data-target");
      if (!targetId) return;

      const panel = document.getElementById(targetId);
      if (!panel) return;

      const isSelected = button.getAttribute("data-state") === "selected";
      panel.hidden = !isSelected;
    });
  }

  private updateIcon(button: HTMLButtonElement, isSelected: boolean): void {
    const statusIconContainer = button.querySelector(
      ".chk-button-payments__status-icon",
    );
    if (!statusIconContainer) return;

    const iconElement = statusIconContainer.firstElementChild;
    if (!iconElement) return;

    const unselectedClass = "dsc-co-icon-radioButtonLg";
    const selectedClass = "dsc-co-icon-radioButtonSelectedCheckLg";

    if (
      iconElement.classList.contains(unselectedClass) ||
      iconElement.classList.contains(selectedClass)
    ) {
      this.swapClass(
        iconElement as HTMLElement,
        unselectedClass,
        selectedClass,
        isSelected,
      );
      return;
    }

    const internalIcon = iconElement.querySelector(
      `.${unselectedClass}, .${selectedClass}`,
    );
    if (internalIcon) {
      this.swapClass(
        internalIcon as HTMLElement,
        unselectedClass,
        selectedClass,
        isSelected,
      );
    }
  }

  private swapClass(
    element: HTMLElement,
    unselected: string,
    selected: string,
    isSelected: boolean,
  ) {
    if (isSelected) {
      element.classList.replace(unselected, selected);
    } else {
      element.classList.replace(selected, unselected);
    }
  }
}

PluginRegistry.register("chk-button-payments", ChkButtonPaymentsPlugin);
